import React, { useEffect, useState } from "react";
import { Container, Card, CardBody, Row, Col, Input, Label, Table } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import logo from "../../assets/images/logo.png";

const CashFlowReport = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Cash Flow Report", link: "#" },
  ]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ income: 0, expense: 0, outstanding: 0 });
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
  });

  const normalizeAmount = (value) => {
    const numeric = Number(
      String(value ?? 0)
        .replace(/[^0-9.-]/g, "")
        .trim()
    );
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const formatAmount = (value) =>
    normalizeAmount(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const fetchCashFlow = async (activeFilters = filters) => {
    try {
      const params = {};
      if (activeFilters.from_date) params.from_date = activeFilters.from_date;
      if (activeFilters.to_date) params.to_date = activeFilters.to_date;

      const result = await apiClient.get("/expense/cash-flow-report", { params });

      const responseRows = result.data.response || [];
      setRows(responseRows);

      const apiTotals = result.data.totals || { income: 0, expense: 0, outstanding: 0 };
      setTotals(apiTotals);
    } catch (err) {
      console.error("Error fetching cash flow report:", err);
      await showAlert({
        title: "Cash Flow Report",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to fetch cash flow report",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    fetchCashFlow();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = () => {
    if (filters.from_date && filters.to_date && filters.from_date > filters.to_date) {
      showAlert({
        title: "Cash Flow Report",
        text: "From date cannot be greater than To date",
        icon: "warning",
      });
      return;
    }
    fetchCashFlow(filters);
  };

  const resetFilters = () => {
    const resetState = { from_date: "", to_date: "" };
    setFilters(resetState);
    fetchCashFlow(resetState);
  };

  const getNetCash = (row) => normalizeAmount(row.income) - normalizeAmount(row.expense);

  const handlePrint = async () => {
    if (!rows.length) {
      await showAlert({
        title: "Cash Flow Report",
        text: "No data available to print",
        icon: "warning",
      });
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const reportRows = rows
      .map(
        (row, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(row.month_key)}</td>
            <td class="amount-cell">${formatAmount(row.income)}</td>
            <td class="amount-cell">${formatAmount(row.expense)}</td>
            <td class="amount-cell">${formatAmount(row.outstanding)}</td>
            <td class="amount-cell">${formatAmount(getNetCash(row))}</td>
          </tr>
        `
      )
      .join("");

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      await showAlert({
        title: "Cash Flow Report",
        text: "Unable to open print window. Please allow popups and try again.",
        icon: "error",
      });
      return;
    }

    const generatedAt = new Date().toLocaleString();

    const printContent = `
      <html>
        <head>
          <title>Cash Flow Report</title>
          <style>
            @page { margin: 22mm 16mm; }
            body { font-family: "Segoe UI", Arial, sans-serif; color: #1f2937; margin: 18px; }
            .header {
              display: block;
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 16px;
              margin-bottom: 16px;
            }
            .header-top {
              display: flex;
              justify-content: center;
              margin-bottom: 12px;
            }
            .logo { width: 120px; height: 120px; object-fit: contain; }
            .report-title {
              margin: 0;
              text-align: center;
              font-size: 30px;
              line-height: 1.15;
              font-weight: 800;
              color: #111827;
            }
            .generated-at {
              margin-top: 8px;
              text-align: center;
              font-size: 12px;
              color: #6b7280;
            }
            .meta {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 8px 18px;
              margin-bottom: 16px;
              font-size: 12px;
            }
            .meta-item strong { color: #111827; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th {
              background: #f3f4f6;
              color: #111827;
              border: 1px solid #d1d5db;
              padding: 8px;
              text-align: left;
            }
            td {
              border: 1px solid #e5e7eb;
              padding: 7px 8px;
              vertical-align: top;
            }
            .amount-cell { text-align: right; white-space: nowrap; }
            tfoot td {
              background: #f9fafb;
              font-weight: 700;
              border-top: 2px solid #9ca3af;
            }
            .footer {
              margin-top: 12px;
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              color: #6b7280;
              border-top: 1px solid #e5e7eb;
              padding-top: 8px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-top">
              <img src="${logo}" alt="Daryeel Logo" class="logo" />
            </div>
            <h1 class="report-title">Monthly Cash Flow Report</h1>
            <div class="generated-at">Generated: ${escapeHtml(generatedAt)}</div>
          </div>

          <div class="meta">
            <div class="meta-item"><strong>From Date:</strong> ${escapeHtml(filters.from_date || "-")}</div>
            <div class="meta-item"><strong>To Date:</strong> ${escapeHtml(filters.to_date || "-")}</div>
            <div class="meta-item"><strong>Total Records:</strong> ${rows.length}</div>
            <div class="meta-item"><strong>Report:</strong> Income / Expense / Outstanding</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Month</th>
                <th>Income</th>
                <th>Expense</th>
                <th>Outstanding</th>
                <th>Net Cash</th>
              </tr>
            </thead>
            <tbody>
              ${reportRows}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2">Totals</td>
                <td class="amount-cell">${formatAmount(totals.income)}</td>
                <td class="amount-cell">${formatAmount(totals.expense)}</td>
                <td class="amount-cell">${formatAmount(totals.outstanding)}</td>
                <td class="amount-cell">${formatAmount(normalizeAmount(totals.income) - normalizeAmount(totals.expense))}</td>
              </tr>
            </tfoot>
          </table>

          <div class="footer">
            <span>Daryeel - Finance Reports</span>
            <span>Printed report</span>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Cash Flow Report" breadcrumbItems={breadcrumbItems} />

        <Card>
          <CardBody>
            <h5 className="mb-3">Filters</h5>
            <Row className="g-3 align-items-end">
              <Col md="4">
                <Label className="form-label fw-semibold">From Date</Label>
                <Input
                  type="date"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="4">
                <Label className="form-label fw-semibold">To Date</Label>
                <Input
                  type="date"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                />
              </Col>
              <Col md="4">
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-primary flex-grow-1" onClick={applyFilters}>
                    Filter
                  </button>
                  <button type="button" className="btn btn-light border flex-grow-1" onClick={resetFilters}>
                    Reset
                  </button>
                  <button type="button" className="btn btn-success flex-grow-1" onClick={handlePrint}>
                    Print
                  </button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h5 className="mb-3">Monthly Cash Flow</h5>
            <div className="table-responsive">
              <Table className="table table-hover align-middle table-nowrap mb-0">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Month</th>
                    <th>Income</th>
                    <th>Expense</th>
                    <th>Outstanding</th>
                    <th>Net Cash</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No report data found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr key={row.month_key}>
                        <td>{index + 1}</td>
                        <td>{row.month_key}</td>
                        <td>{formatAmount(row.income)}</td>
                        <td>{formatAmount(row.expense)}</td>
                        <td>{formatAmount(row.outstanding)}</td>
                        <td>{formatAmount(getNetCash(row))}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan="2">Totals</th>
                    <th>{formatAmount(totals.income)}</th>
                    <th>{formatAmount(totals.expense)}</th>
                    <th>{formatAmount(totals.outstanding)}</th>
                    <th>{formatAmount(normalizeAmount(totals.income) - normalizeAmount(totals.expense))}</th>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default CashFlowReport;
